import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiConnectorPost } from "../../services/apiconnector";
import { endpoint } from "../../services/urls";

export default function BonusHistory({ subLabel = "LEVEL", title = null }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const isLevel = subLabel === "LEVEL";
  //  title   = title || (isLevel ? "Level Bonus History" : "Reward History");
  const icon = isLevel ? "🏅" : "🎁";

  const { data, isLoading } = useQuery({
    queryKey: ["bonus_history", subLabel, page],
    queryFn: () =>
      apiConnectorPost(endpoint.get_report_details, {
        search: "",
        start_date: "",
        end_date: "",
        page,
        count: 10,
        sub_label: subLabel,
        main_label: "ALL",
        is_global: false,
        onlyP2PTransfer: false,
      }),
    keepPreviousData: true,
  });

  const rows = data?.data?.result?.data || [];
  const totalPages = Math.max(1, data?.data?.result?.total_pages || 1);

  return (
    <div style={s.root}>
      <div style={s.grid} />
      <div style={s.glowTL} />
      <div style={s.glowBR} />

      <div style={s.container}>
        {/* ── Header ── */}
        <div style={s.header}>
          <button style={s.backBtn} onClick={() => navigate("/Promotion")}>
            ‹
          </button>
          <div style={s.titleWrap}>
            <span style={s.titleIcon}>{icon}</span>
            <h1 style={s.title}>{title}</h1>
          </div>
        </div>

        {/* ── Content ── */}
        {isLoading ? (
          <div style={s.center}>
            <div style={s.spinner} />
          </div>
        ) : rows.length === 0 ? (
          <div style={s.center}>
            <span style={{ fontSize: 40 }}>{icon}</span>
            <p style={s.emptyText}>No records found</p>
          </div>
        ) : (
          <>
            {rows.map((row, i) => (
              <div key={row?.tr07_trans_id || i} style={s.card}>
                {/* TX id + amount */}
                <div style={s.cardRow}>
                  <span style={s.txId}>#{row?.tr07_trans_id || "-"}</span>
                  <span style={s.amount}>
                    ${parseFloat(row?.tr07_tr_amount || 0).toFixed(2)} USDT
                  </span>
                </div>

                {/* Date */}
                <p style={s.date}>{fmtDate(row?.tr07_created_at)}</p>

                {/* Label + status */}
                <div style={s.cardRow}>
                  <span style={s.badge}>{row?.tr07_sub_label || subLabel}</span>
                  <span style={s.statusBadge}>✓ Success</span>
                </div>

                {/* Description */}
                {row?.tr07_description && (
                  <p style={s.desc}>{row.tr07_description}</p>
                )}
              </div>
            ))}

            {/* ── Pagination ── */}
            {totalPages > 1 && (
              <div style={s.pagination}>
                <button
                  style={{
                    ...s.pageBtn,
                    ...(page === 1 ? s.pageBtnDisabled : {}),
                  }}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  ‹ Prev
                </button>
                <span style={s.pageInfo}>
                  {page} / {totalPages}
                </span>
                <button
                  style={{
                    ...s.pageBtn,
                    ...(page === totalPages ? s.pageBtnDisabled : {}),
                  }}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next ›
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=Sora:wght@300;400;500;600;700&display=swap');
        @keyframes spin   { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
      `}</style>
    </div>
  );
}

function fmtDate(str) {
  if (!str) return "-";
  const d = new Date(str);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const C = {
  bg: "#020c12",
  card: "rgba(4,16,24,0.90)",
  border: "rgba(0,229,255,0.10)",
  cyan: "#00e5ff",
  cyanDim: "rgba(0,229,255,0.4)",
  text: "#e0f7fa",
  muted: "rgba(100,180,200,0.5)",
  green: "#00e676",
};

const s = {
  root: {
    minHeight: "100vh",
    background: C.bg,
    fontFamily: "'Sora',sans-serif",
    position: "relative",
    overflow: "hidden",
    paddingBottom: 100,
  },
  grid: {
    position: "fixed",
    inset: 0,
    pointerEvents: "none",
    backgroundImage:
      "linear-gradient(rgba(0,229,255,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(0,229,255,0.03) 1px,transparent 1px)",
    backgroundSize: "44px 44px",
  },
  glowTL: {
    position: "fixed",
    top: -150,
    left: -100,
    width: 480,
    height: 480,
    borderRadius: "50%",
    pointerEvents: "none",
    background:
      "radial-gradient(circle,rgba(0,229,255,0.08) 0%,transparent 70%)",
  },
  glowBR: {
    position: "fixed",
    bottom: -100,
    right: -80,
    width: 380,
    height: 380,
    borderRadius: "50%",
    pointerEvents: "none",
    background:
      "radial-gradient(circle,rgba(0,180,200,0.05) 0%,transparent 70%)",
  },
  container: {
    maxWidth: 480,
    margin: "0 auto",
    padding: "24px 16px 40px",
    position: "relative",
    zIndex: 10,
    animation: "fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) both",
  },

  header: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 20,
  },
  backBtn: {
    width: 38,
    height: 38,
    borderRadius: 10,
    flexShrink: 0,
    border: "1px solid rgba(0,229,255,0.2)",
    background: "rgba(0,229,255,0.06)",
    color: C.cyan,
    fontSize: 22,
    lineHeight: 1,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  titleWrap: { display: "flex", alignItems: "center", gap: 8 },
  titleIcon: { fontSize: 20 },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: C.text,
    letterSpacing: "-0.02em",
  },

  center: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 0",
    gap: 14,
  },
  spinner: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    border: "3px solid rgba(0,229,255,0.15)",
    borderTopColor: C.cyan,
    animation: "spin 0.75s linear infinite",
  },
  emptyText: {
    color: C.muted,
    fontSize: 13,
    fontFamily: "'Space Mono',monospace",
  },

  card: {
    background: "rgba(6,20,30,0.92)",
    border: "1px solid rgba(0,229,255,0.18)",
    borderRadius: 16,
    padding: "14px 16px",
    marginBottom: 10,
    backdropFilter: "blur(20px)",
    boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
  },
  cardRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 7,
  },
  txId: {
    color: "rgba(160,220,235,0.8)",
    fontSize: 11,
    fontFamily: "'Space Mono',monospace",
  },
  amount: {
    color: C.cyan,
    fontWeight: 700,
    fontSize: 15,
    fontFamily: "'Space Mono',monospace",
  },
  date: { color: "rgba(160,220,235,0.75)", fontSize: 11, marginBottom: 8 },
  badge: {
    background: "rgba(0,229,255,0.18)",
    border: "1px solid rgba(0,229,255,0.45)",
    borderRadius: 6,
    padding: "4px 12px",
    color: C.cyan,
    fontSize: 11,
    fontWeight: 700,
  },
  statusBadge: {
    background: "rgba(0,230,118,0.18)",
    border: "1px solid rgba(0,230,118,0.45)",
    borderRadius: 6,
    padding: "4px 12px",
    color: C.green,
    fontSize: 11,
    fontWeight: 700,
  },
  desc: {
    color: "rgba(190,235,245,0.85)",
    fontSize: 11,
    marginTop: 8,
    background: "rgba(0,229,255,0.05)",
    borderRadius: 6,
    padding: "7px 10px",
    border: "1px solid rgba(0,229,255,0.15)",
  },

  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    marginTop: 16,
  },
  pageBtn: {
    background: "rgba(0,229,255,0.06)",
    border: "1px solid rgba(0,229,255,0.2)",
    borderRadius: 8,
    color: C.cyan,
    fontSize: 12,
    padding: "7px 16px",
    cursor: "pointer",
    fontFamily: "'Space Mono',monospace",
  },
  pageBtnDisabled: {
    color: "rgba(100,180,200,0.2)",
    border: "1px solid rgba(0,229,255,0.06)",
    cursor: "not-allowed",
  },
  pageInfo: {
    color: C.muted,
    fontSize: 12,
    fontFamily: "'Space Mono',monospace",
  },
};
